import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ContentContainer,
  ModalTitle,
  FormContainer,
  InputWrapper,
  FieldsRow,
  FieldHalf,
  ButtonsContainer,
  CancelButton,
  SubmitButton,
} from "./AddCustomProcedureModal.styled";
import { Select } from "../../Select";
import TextInput from "../../TextInput/TextInput";
import { FormErrorContainer } from "../../FormErrorContainer";
import { useApiErrorHandling } from "../../../hooks";
import { SelectOption } from "../../Select/Select";
import { cdtCodesService, CDTCode } from "../../../services/cdtCodesService";
import {
  normalizeFeeOnBlur,
  sanitizeFeeInput,
} from "../../../utils/feeInputUtils";

export interface CustomProcedureFormData {
  name: string;
  cdtCodeIds: number[];
  fee: number;
  durationMinutes: number;
  description: string;
}

interface AddCustomProcedureModalContentProps {
  onAdd: (data: CustomProcedureFormData) => void | Promise<void>;
  onClose: () => void;
}

const AddCustomProcedureModalContent: React.FC<
  AddCustomProcedureModalContentProps
> = ({ onAdd, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CustomProcedureFormData>({
    name: "",
    cdtCodeIds: [],
    fee: 0,
    durationMinutes: 0,
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Raw string values shown in the inputs while the user is typing.
  // Committed (numeric) values live in formData; display syncs on blur.
  const [durationDisplay, setDurationDisplay] = useState("");
  const [feeDisplay, setFeeDisplay] = useState("");
  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling();
  const [cdtCodes, setCdtCodes] = useState<CDTCode[]>([]);
  const [cdtLoading, setCdtLoading] = useState(true);

  // Fetch CDT codes on mount
  useEffect(() => {
    const fetchCdtCodes = async () => {
      try {
        setCdtLoading(true);
        const codes = await cdtCodesService.getCDTCodes({ activeOnly: true });
        setCdtCodes(codes);
      } catch (error) {
        console.error("Error fetching CDT codes:", error);
      } finally {
        setCdtLoading(false);
      }
    };
    fetchCdtCodes();
  }, []);

  // CDT options for select
  const cdtOptions: SelectOption[] = useMemo(
    () =>
      cdtCodes.map((code) => ({
        value: code.code,
        label: `${code.code} - ${code.description}`,
        chipLabel: code.code,
      })),
    [cdtCodes],
  );

  // Get selected CDT code strings from IDs
  const selectedCdtCodeStrings = useMemo(() => {
    return formData.cdtCodeIds
      .map((id) => {
        const code = cdtCodes.find((c) => c.id === id);
        return code?.code || "";
      })
      .filter(Boolean);
  }, [formData.cdtCodeIds, cdtCodes]);

  const handleInputChange = (
    field: keyof CustomProcedureFormData,
    value: string | number | number[],
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle CDT code selection change
  const MAX_CDT_CODES = 4;

  const handleCdtCodeChange = (
    value: string | number | (string | number)[],
  ) => {
    const codeStrings = Array.isArray(value)
      ? (value as string[])
      : [value as string];

    const codeIds = codeStrings
      .map((codeStr) => {
        const cdtCode = cdtCodes.find((c) => c.code === codeStr);
        return cdtCode?.id || 0;
      })
      .filter((id) => id > 0);

    handleInputChange("cdtCodeIds", codeIds);

    // Auto-calculate fee and duration from selected codes
    const selectedCodesData = codeIds
      .map((id) => cdtCodes.find((c) => c.id === id))
      .filter(Boolean) as CDTCode[];

    if (selectedCodesData.length > 0) {
      const totalFee = selectedCodesData.reduce(
        (sum, code) => sum + (code.defaultFee || 0),
        0,
      );
      const totalDuration = selectedCodesData.reduce(
        (sum, code) => sum + (code.defaultDurationMinutes || 0),
        0,
      );

      setFormData((prev) => ({
        ...prev,
        cdtCodeIds: codeIds,
        fee: totalFee,
        durationMinutes: totalDuration,
      }));
      setFeeDisplay(String(totalFee));
      setDurationDisplay(String(totalDuration));
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    const hasNameError = !formData.name.trim();
    const hasCdtRequiredError = formData.cdtCodeIds.length === 0;
    const hasCdtMaxError = formData.cdtCodeIds.length > MAX_CDT_CODES;

    if (hasNameError || hasCdtRequiredError || hasCdtMaxError) {
      return;
    }

    clearErrors();
    try {
      setIsSubmitting(true);
      await onAdd(formData);
      onClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const nameErrorMsg =
    submitted && !formData.name.trim()
      ? t("addCustomProcedureModal.nameRequired", "Procedure name is required")
      : undefined;

  const cdtCodeErrorMsg =
    formData.cdtCodeIds.length > MAX_CDT_CODES
      ? t(
          "addCustomProcedureModal.cdtCodeMaxError",
          "You can select a maximum of 4 CDT codes.",
        )
      : submitted && formData.cdtCodeIds.length === 0
        ? t(
            "addCustomProcedureModal.cdtCodeRequired",
            "Please select at least one CDT code",
          )
        : undefined;

  // Combine client validation errors with server field errors for highlighting
  const hasNameError = !!nameErrorMsg || !!fieldErrors["name"]?.length;
  const hasCdtError = !!cdtCodeErrorMsg || !!fieldErrors["cdtCodeIds"]?.length;

  const validationErrors = [nameErrorMsg, cdtCodeErrorMsg].filter(
    (e): e is string => !!e,
  );
  const allErrors = [...validationErrors, ...apiErrors];

  return (
    <ContentContainer>
      <ModalTitle>
        {t("addCustomProcedureModal.title", "Add Custom Procedure")}
      </ModalTitle>

      <FormContainer>
        <InputWrapper>
          <TextInput
            label={t("addCustomProcedureModal.procedureName", "Procedure Name")}
            placeholder={t(
              "addCustomProcedureModal.procedureNamePlaceholder",
              "Enter procedure name",
            )}
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            disabled={isSubmitting}
            error={hasNameError}
            required
          />
        </InputWrapper>

        <InputWrapper>
          <Select
            label={t("addCustomProcedureModal.cdtCode", "CDT Code")}
            placeholder={t(
              "addCustomProcedureModal.cdtCodePlaceholder",
              "Select CDT codes",
            )}
            value={selectedCdtCodeStrings}
            onChange={handleCdtCodeChange}
            options={cdtOptions}
            multiple
            searchable
            showChips
            loading={cdtLoading}
            fullWidth
            error={hasCdtError}
            required
          />
        </InputWrapper>

        <FieldsRow>
          <FieldHalf>
            <TextInput
              label={t("addCustomProcedureModal.duration", "Duration, min")}
              placeholder="0"
              type="number"
              value={durationDisplay}
              onChange={(e) => setDurationDisplay(e.target.value)}
              onBlur={() => {
                const parsed =
                  durationDisplay === ""
                    ? 0
                    : parseInt(durationDisplay) || 0;
                setDurationDisplay(String(parsed));
                handleInputChange("durationMinutes", parsed);
              }}
              disabled={isSubmitting}
            />
          </FieldHalf>
          <FieldHalf>
            <TextInput
              label={t("addCustomProcedureModal.fee", "Fee, $")}
              placeholder="0"
              type="text"
              value={feeDisplay}
              onChange={(e) => setFeeDisplay(sanitizeFeeInput(e.target.value))}
              onBlur={() => {
                const normalized = normalizeFeeOnBlur(feeDisplay);
                setFeeDisplay(normalized);
                handleInputChange("fee", parseFloat(normalized) || 0);
              }}
              disabled={isSubmitting}
            />
          </FieldHalf>
        </FieldsRow>

        <InputWrapper>
          <TextInput
            label={t("addCustomProcedureModal.description", "Description")}
            placeholder={t(
              "addCustomProcedureModal.descriptionPlaceholder",
              "Add description...",
            )}
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            disabled={isSubmitting}
            multiline
            rows={4}
          />
        </InputWrapper>
      </FormContainer>

      <FormErrorContainer errors={allErrors} style={{ marginTop: 12 }} />

      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {t("addCustomProcedureModal.cancel", "Cancel")}
        </CancelButton>
        <SubmitButton
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("addCustomProcedureModal.addProcedure", "Add Procedure")
          )}
        </SubmitButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default AddCustomProcedureModalContent;
